import type { Component, JSX } from 'solid-js'
import { AboutDialog } from '~/components/AboutDialog'
import { Logo } from '~/components/Logo'
import { OnAirBadge } from '~/components/OnAirBadge'
import { ViewerCount } from '~/components/ViewerCount'
import { SITE } from '~/lib/site'

type Props = {
  live?: boolean
  /** いま同じ放送を見ている人数 */
  viewers?: number
  /** 同時視聴の接続が生きているか */
  viewersConnected?: boolean
  /** 全画面（シアター）モード。ヘッダーとフッターを畳んで映像だけにする */
  theater?: boolean
  onToggleTheater?: () => void
  children?: JSX.Element
}

/**
 * 1 画面に収める外枠（テレビをつけている状態に近づけるため、ページ自体はスクロールさせない）。
 *
 * ヘッダーとフッターの高さを引いた残りを main に渡し、
 * スクロールが要る部分（コメント・番組表）だけを内側でスクロールさせる。
 */
export const Layout: Component<Props> = (props) => {
  return (
    <div class="grid h-dvh grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
      <header
        class="flex min-w-0 items-center justify-between px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] sm:px-6"
        classList={{ hidden: props.theater }}
      >
        <a href="/" class="flex min-w-0 items-center gap-2.5" aria-label={SITE.name}>
          <Logo class="h-6 w-auto shrink-0" />
          <h1 class="min-w-0 text-base font-bold tracking-[0.12em]">
            834<span class="text-ch-accent">CH</span>
            {/* 狭い画面ではフッターを畳むので、非公式である表示はここが受け持つ（要件定義 14 章） */}
            <span class="ml-1.5 text-[11px] font-medium tracking-normal text-ch-dim">非公式</span>
            <span class="sr-only"> サカナクション・山口一郎のネットテレビ</span>
          </h1>
        </a>
        <div class="flex shrink-0 items-center gap-3">
          <ViewerCount count={props.viewers ?? 0} connected={props.viewersConnected === true} />
          <OnAirBadge live={props.live} />
          <button
            type="button"
            onClick={() => props.onToggleTheater?.()}
            aria-label="全画面で見る"
            title="全画面で見る（F）"
            class="text-ch-dim transition-colors hover:text-ch-text"
          >
            <svg viewBox="0 0 16 16" class="size-4 fill-current" aria-hidden="true">
              <path d="M2 2h5v1.5H3.5V7H2V2Zm7 0h5v5h-1.5V3.5H9V2ZM2 9h1.5v3.5H7V14H2V9Zm10.5 0H14v5H9v-1.5h3.5V9Z" />
            </svg>
          </button>
        </div>
      </header>

      {/*
        行を明示する。ヘッダーとフッターを display:none で畳むとグリッドの項目から
        外れてしまい、行を指定していないと main が先頭の auto 行に落ちて、
        内容ぶんの高さしか持たなくなる（全画面で Player が潰れる）
      */}
      <main class="row-start-2 min-h-0 min-w-0 overflow-hidden">{props.children}</main>

      <footer
        class="flex min-w-0 items-center justify-between gap-3 border-t border-ch-border px-4 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))] text-[10px] text-ch-dim sm:px-6"
        classList={{ hidden: props.theater }}
      >
        {/* 狭い画面では読み切れずに切れるだけなので、短い方だけを出す */}
        <p class="hidden truncate sm:block">
          834CH は非公式のファンサイトです。動画は YouTube 公式プレイヤーで再生しています。
        </p>
        <p class="truncate sm:hidden">動画は YouTube 公式プレイヤーで再生しています。</p>
        <AboutDialog />
      </footer>
    </div>
  )
}
